import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
} from 'src/app/core/dx-types';
import { GdIcons } from 'src/app/ui/app-gd.icons';

import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	constructDatablockList,
	DatablockSelector,
	navigateToCreateFromDatablock,
	navigateToDatablock,
	navigateToDatablockCreate,
	navigateToDatablockDuplicate,
	navigateToExportDatablock,
	openDeleteConfirmationPopup,
	openPersistConfirmationPopup,
	openRepersistConfirmationPopup,
	openUnpersistConfirmationPopup,
	refreshDatablockList,
	stopPersistence,
	toggleDatablockStatus,
	updateIncludePublic,
} from '../../store';
import { DatablockItemEntity } from './datablock-item.entity';
import { DatablockListEntity } from './datablock-list.entity';

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

@Component({
	selector: 'app-datablock-list',
	templateUrl: './datablock-list.component.html',
	styleUrls: ['./datablock-list.component.scss'],
})
export class DatablockListComponent extends DcBaseComponent implements OnInit {
	protected readonly GdIcons = GdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public openedOverlayId = -1;

	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'expose',
			description: $localize`:i18n=@@datablock.list.action.expose:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.exposeDatablock(datablock.id);
			},
			icon: ActionIcons.ExposeDatablock,
		},
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@datablock.list.action.duplicate:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.duplicateDatablock(datablock.id);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.toggleActivationStatus(datablock);
			},
			icon: ActionIcons.ActivateElement,
		},
	];
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'expose',
			description: $localize`:i18n=@@datablock.list.action.expose:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.exposeDatablock(datablock.id);
			},
			icon: ActionIcons.ExposeDatablock,
		},
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@datablock.list.action.duplicate:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.duplicateDatablock(datablock.id);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.toggleActivationStatus(datablock);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];

	public isPersistedHeaderFilter = [
		{
			text: $localize`:i18n=@@datablock.list.column.persistance.persisted:`,
			value: ['isPersisted', '=', true],
		},
		{
			text: $localize`:i18n=@@datablock.list.column.persistance.unpersisted:`,
			value: ['isPersisted', '=', false],
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: DatablockListEntity;
		includePublic: boolean;
		isLoadingPersistence: boolean;
	}>;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	private readonly datablockListSubject =
		new ReplaySubject<DatablockListEntity>(1);

	private readonly loadTriggerSubject = new BehaviorSubject<boolean>(true);

	protected readonly ComponentIcons = ComponentIcons;
	public constructor(
		private readonly store: Store,
		private readonly datablockSelector: DatablockSelector,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'datablock-list';
		this.dxLocalStorageKey = 'dx.grid.datablock-list';

		this.paths = [
			{
				label: $localize`:i18n=@@datablock.list.title:`,
				isActive: false,
				callback: (): void => {},
			},
		];

		this.vo$ = combineAll({
			isFirstLoad: this.loadTriggerSubject,
			isLoading: this.datablockSelector.getLoading$(),
			list: this.datablockListSubject,
			includePublic: this.datablockSelector.includePublic$(),
			isLoadingPersistence: this.datablockSelector.getLoadingPersistence$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(constructDatablockList());

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.datablockSelector
			.getDatablockList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();

					this.datablockListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
		this.datablockSelector
			.getLoading$()
			.pipe(
				tap((isLoading) =>
					isLoading
						? this.grid?.instance.beginCustomLoading('')
						: this.grid?.instance.endCustomLoading()
				)
			)
			.subscribe();
	}

	public refresh(): void {
		this.loadTriggerSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshDatablockList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}
	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const loadTrigger = await firstValueFrom(this.loadTriggerSubject);

		if (loadTrigger) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.loadTriggerSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public createNewDatablock(): void {
		this.store.dispatch(navigateToDatablockCreate());
	}

	public stopPersistence(event: Event, datablock: DatablockItemEntity): void {
		event.stopPropagation();
		if (datablock.persistenceState.recentJobId === null) {
			return;
		}
		this.store.dispatch(
			stopPersistence({
				jobKey: datablock.persistenceState.recentJobId,
			})
		);
	}

	public openPersistencePopover(
		event: Event,
		datablock: DatablockItemEntity
	): void {
		event.stopPropagation();
		this.openedOverlayId = datablock.id;
	}

	public closePersistencePopover(): void {
		this.openedOverlayId = -1;
	}

	public navigateToDatablock(id: number): void {
		this.store.dispatch(
			navigateToDatablock({
				id,
			})
		);
	}

	public createFromDatablock(event: Event, id: number): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToCreateFromDatablock({
				id,
			})
		);
	}

	public exportDatablock(event: Event, datablock: DatablockItemEntity): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToExportDatablock({
				id: datablock.id,
				label: datablock.label,
			})
		);
	}

	public exposeDatablock(id: number): void {
		// FIXME
	}

	public duplicateDatablock(id: number): void {
		this.store.dispatch(
			navigateToDatablockDuplicate({
				id,
			})
		);
	}

	public deleteDatablock(event: Event, datablock: DatablockItemEntity): void {
		event.stopPropagation();
		this.store.dispatch(
			openDeleteConfirmationPopup({
				datablockId: [datablock.id],
				datablockLabel: datablock.label,
			})
		);
	}

	public persistDatablock(datablock: DatablockItemEntity): void {
		this.store.dispatch(
			openPersistConfirmationPopup({
				datablockId: [datablock.id],
				datablockCode: [datablock.code],
				datablockLabel: datablock.label,
			})
		);
	}

	public unpersistDatablock(datablock: DatablockItemEntity): void {
		this.store.dispatch(
			openUnpersistConfirmationPopup({
				datablockId: [datablock.id],
				datablockCode: [datablock.code],
				datablockLabel: datablock.label,
			})
		);
	}

	public repersistDatablock(datablock: DatablockItemEntity): void {
		this.store.dispatch(
			openRepersistConfirmationPopup({
				datablockId: datablock.id,
				datablockCode: datablock.code,
				datablockLabel: datablock.label,
			})
		);
	}

	public toggleIncludePublic(value: MatSelectChange): void {
		this.store.dispatch(
			updateIncludePublic({
				includePublic: value.value,
			})
		);
	}

	public toggleActivationStatus(datablock: DatablockItemEntity): void {
		this.store.dispatch(
			toggleDatablockStatus({
				datablockId: datablock.id,
			})
		);
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedDatablocks = await firstValueFrom(
			this.datablockSelector.getSelectedDatablocks$(selectedRowKeys)
		);
		if (selectedDatablocks.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					datablockId: [selectedDatablocks[0].id],
					datablockLabel: selectedDatablocks[0].label,
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				datablockId: selectedDatablocks.map((k) => k.id),
				datablockLabel: '',
			})
		);
	}
	private async persistInDiscSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedDatablocks = await firstValueFrom(
			this.datablockSelector.getSelectedDatablocks$(selectedRowKeys)
		);
		if (selectedDatablocks.length === 1) {
			return this.store.dispatch(
				openPersistConfirmationPopup({
					datablockId: [selectedDatablocks[0].id],
					datablockCode: [selectedDatablocks[0].code],
					datablockLabel: selectedDatablocks[0].label,
				})
			);
		}
		return this.store.dispatch(
			openPersistConfirmationPopup({
				datablockId: selectedDatablocks.map((k) => k.id),
				datablockCode: selectedDatablocks.map((k) => k.code),
				datablockLabel: '',
			})
		);
	}
	private async unpersistSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedDatablocks = await firstValueFrom(
			this.datablockSelector.getSelectedDatablocks$(selectedRowKeys)
		);
		if (selectedDatablocks.length === 1) {
			return this.store.dispatch(
				openUnpersistConfirmationPopup({
					datablockId: [selectedDatablocks[0].id],
					datablockCode: [selectedDatablocks[0].code],
					datablockLabel: selectedDatablocks[0].label,
				})
			);
		}
		return this.store.dispatch(
			openUnpersistConfirmationPopup({
				datablockId: selectedDatablocks.map((k) => k.id),
				datablockCode: selectedDatablocks.map((k) => k.code),
				datablockLabel: '',
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DatablockItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DatablockItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
			ActionIcons.MoveDatablock,
			ActionIcons.PersistInDisk,
			ActionIcons.ExposeDatablock,
			ActionIcons.UnPersist,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@datablock.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.persistInDiscSelected(),
				iconName: actionMap.get(ActionIcons.PersistInDisk)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@datablock.list.diskPersistence:`,
				typed: 'plain-context-action',
			},
			{
				order: 2,
				command: (): Promise<void> => this.unpersistSelected(),
				iconName: actionMap.get(ActionIcons.UnPersist)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@datablock.list.action.depersistAll:`,
				typed: 'plain-context-action',
			},
			{
				order: 3,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@datablock.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
